import React from "react";
import { Link } from "react-router-dom";
import { useClearance } from "../../../api/queries/assetQueries";
import { getApiErrorMessage, getApiErrorStatus } from "../../../utils/apiError";
import { formatDateTime } from "../../../utils/dateTime";
import { AssignmentStatusBadge, ConditionText } from "./AssetBadges";
import { useAssetActions } from "./AssetActionDialogs";
import { ExpectedReturn, AcknowledgementText } from "./AssetDisplay";

/**
 * Offboarding clearance for one employee.
 * canManage: show Return buttons (managers). Employees see it read-only.
 */
const EmployeeClearance = ({ employeeId, canManage = false }) => {
  const query = useClearance(employeeId);
  const { openAction, dialogs } = useAssetActions();
  const data = query.data;

  if (query.isLoading) {
    return (
      <span
        className="spinner-border spinner-border-sm text-primary"
        role="status"
        aria-label="Loading clearance"
      ></span>
    );
  }
  if (query.isError) {
    const forbidden = getApiErrorStatus(query.error) === 403;
    return (
      <p className={forbidden ? "text-slate-500 m-0" : "text-danger m-0"}>
        {forbidden
          ? "You don't have access to this employee's assets."
          : getApiErrorMessage(query.error, "Could not load the clearance.")}
      </p>
    );
  }
  if (!data) return null;

  const { clearance = {}, active = [], history = [] } = data;
  const lostItems = history.filter((h) => h.status === "LOST");

  return (
    <div className="d-flex flex-column gap-3">
      <div
        className={`clearance-status ${clearance.canClear ? "clearance-status--ok" : "clearance-status--blocked"}`}
      >
        <i
          className={`bi ${clearance.canClear ? "bi-check-circle-fill" : "bi-x-circle-fill"}`}
        ></i>
        <div>
          <div className="fw-bold">
            {clearance.canClear
              ? "No assets to return"
              : `${clearance.unreturned} asset${clearance.unreturned === 1 ? "" : "s"} not returned`}
          </div>
          {data.employee?.employeeStatus && (
            <div className="fs-075 opacity-80">
              Employee status: {data.employee.employeeStatus}
            </div>
          )}
        </div>
      </div>

      {clearance.lost > 0 && (
        <div className="alert alert-warning m-0 fs-085" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {clearance.lost} asset{clearance.lost === 1 ? " was" : "s were"}{" "}
          reported lost:{" "}
          {lostItems
            .map((l) => l.asset?.assetTag)
            .filter(Boolean)
            .join(", ")}
        </div>
      )}

      {active.length > 0 && (
        <div>
          <div className="profile-label mb-2">Not yet returned</div>
          <ul className="clearance-list">
            {active.map((a) => (
              <li key={a.id}>
                <div className="flex-grow-1">
                  <Link
                    to={`/assets/${a.asset?.id}`}
                    className="fw-bold text-navy text-decoration-none"
                  >
                    {a.asset?.assetTag}
                  </Link>{" "}
                  <span>{a.asset?.name}</span>
                  <div className="fs-075 text-slate-500">
                    Since {formatDateTime(a.assignedAt)} · Return:{" "}
                    <ExpectedReturn date={a.expectedReturnDate} /> ·{" "}
                    <AcknowledgementText acknowledgedAt={a.acknowledgedAt} />
                  </div>
                </div>
                {canManage && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary profile-btn"
                    onClick={() =>
                      openAction("return", { asset: a.asset, assignment: a })
                    }
                  >
                    <i className="bi bi-box-arrow-in-left"></i> Return
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {history.length > 0 && (
        <div>
          <div className="profile-label mb-2">History</div>
          <ul className="clearance-list clearance-list--muted">
            {history.map((h) => (
              <li key={h.id}>
                <div className="flex-grow-1">
                  <span className="fw-semibold">{h.asset?.assetTag}</span>{" "}
                  <span>{h.asset?.name}</span>
                  <div className="fs-075 text-slate-500">
                    {formatDateTime(h.assignedAt)} →{" "}
                    {h.returnedAt ? formatDateTime(h.returnedAt) : "—"}
                    {h.conditionIn && (
                      <>
                        {" "}
                        · returned <ConditionText condition={h.conditionIn} />
                      </>
                    )}
                  </div>
                </div>
                <AssignmentStatusBadge status={h.status} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {dialogs}
    </div>
  );
};

export default EmployeeClearance;
